<template>
    <AppLoadingLayout v-if="isLoading" />
    <div v-else>
        <ModalContainer />
        <AppHealthChecker :checkedParts="checkedParts">
            <Layout />
        </AppHealthChecker>
        <UseFreeToPlayEligibilityChecker />
        <SdsTooltipOverlay />
    </div>
</template>

<script>
    import { getConfig } from '@superbet-group/web.lib.config';
    import { MaintenanceType } from '@superbet-group/web.types';
    import { isMobileSafari } from '@superbet-group/web.lib.vue-utilities';
    import {
        mapGetters,
        mapActions,
    } from 'vuex';
    // registers global components
    import '@shared/components';

    import { directives } from '@app/directives';
    import AppLoadingLayout from '@app/layout/AppLoadingLayout.vue';
    import Layout from '@app/layout/Layout';
    import { getScrollbarWidth } from '@app/layout/helpers';
    import ModalContainer from '@app/modals/ModalContainer';
    import {
        areRoutesRegistered,
        markRoutesAsRegistered,
        shouldWaitForRouteRegistration,
    } from '@app/router/routeRegistration';
    import { onAppInit } from '@app/setup';
    import { enterMaintenance } from '@app/utils';
    import StoreUtil from '@store/StoreUtil';
    import { errorGuard } from '@utils/ErrorGuard';
    import { Product } from '@app/router/products/Product';
    import { setIsNewBoostedOddDisplayEnabled } from '@app/shared/featureFlags/newBoostedOddDisplay';
    import { setPe131DescriptiveOddButtonVariant } from '@app/shared/featureFlags/pe131DescriptiveOddButtonFlag';
    import { PE131DescriptiveOddNameVariant } from '@core/models/user/flags';
    import { initializeFeatureFlagMultiContext } from '@app/shared/featureFlags/initializeFeatureFlagMultiContext.ts';
    import { featureFlagService } from '@superbet-group/web.lib.flags';
    import { AccountFlagNames } from '@modules/userProfile/account/FeatureFlags';
    import UseFreeToPlayEligibilityChecker from '@gamingFederation/UseFreeToPlayEligibilityChecker.vue';
    import { isModalShownOnCurrentRoute } from '@store/modules/ui/shared/modal/helpers';
    import SdsTooltipOverlay from '@modules/shared/components/tooltip/SdsTooltipOverlay.vue';
    import { RouteName } from './app/router/constants/RouteName';
    import {
        setIsDeviceIdEnabledInitial,
        setIsDeviceIdEnabledSubscribed,
    } from '@app/shared/featureFlags/isEnabledByDeviceIdTest';
    import { loadLatestCategoryIconMappings } from '@sportOfferFederation/flags/flagMapping';
    import adjustNapoleonFavicons from './adjustNapoleonFavicons';
    import { addTimeoutToPromise } from './app/utils/async';
    import {
        Brand,
        Theme,
    } from '@superbet-group/web.lib.ui-components';
    import { WikiPath } from '@modules/wiki/WikiPath';
    import { navigateToWiki } from '@modules/wiki/navigateToWiki';
    import { loadExperimentService } from '@app/shared/components/Experiment/internal/loadExperimentService';
    import { Analytics as AnalyticsSDK } from '@superbet-group/web.analytics.sdk';
    import { ClickName } from '@superbet-group/product.analytics';
    import { getDeviceBenchmark } from '@core/utils/performance';

    export default {
        name: 'App',
        data() {
            return {
                checkedParts: [MaintenanceType.Global],
            };
        },
        // eslint-disable-next-line sonarjs/cognitive-complexity
        async created() {
            let config;
            try {
                config = getConfig();
                this.initializeConfig(config);
                this.fetchPaymentProvidersLogos();
            } catch (e) {
                enterMaintenance();
                return;
            }

            await Promise.all([
                initializeFeatureFlagMultiContext(),
                addTimeoutToPromise(
                    window.mGeoJS?.start(),
                    10000,
                ),
            ]);

            try {
                await onAppInit(this.$store);
                loadLatestCategoryIconMappings();
            } finally {
                markRoutesAsRegistered();
            }

            AnalyticsSDK.getInstance().log('click', {
                click_name: ClickName.CLICK_NAME_UNSPECIFIED,
                click: {
                    click_name: 'device_hardware_specifications',
                    type: 'test_click',
                    click_value_1: navigator.deviceMemory || 'unavailable',
                    click_value_2: navigator.hardwareConcurrency || 'unavailable',
                    click_value_3: (navigator.connection || navigator.mozConnection || navigator.webkitConnection)?.effectiveType || 'unavailable',
                    click_value_4: getDeviceBenchmark(),
                    click_value_5: navigator.userAgent || 'unavailable',
                },
            });

            this.$store.dispatch('data/maintenance/initialize');

            featureFlagService.getFeatureValue('web.new-bet-boost-display', false).then((isNewBetBoostDisplayEnabled) => {
                return setIsNewBoostedOddDisplayEnabled(isNewBetBoostDisplayEnabled);
            }).catch(() => {
            });

            const whichPe131Variant = await featureFlagService.getFeatureValue(
                AccountFlagNames.pe131DescriptiveOddName,
                PE131DescriptiveOddNameVariant.None,
            );
            setPe131DescriptiveOddButtonVariant(whichPe131Variant);

            // TODO: LD device id test, remove all code when done
            featureFlagService.subscribeToFeatureChanges('web.test.isEnabledByDeviceId', (isEnabledByDeviceId) => {
                setIsDeviceIdEnabledSubscribed(isEnabledByDeviceId);
            }, false);
            featureFlagService.getFeatureValue('web.test.isEnabledByDeviceId', false).then((isEnabledByDeviceId) => {
                return setIsDeviceIdEnabledInitial(isEnabledByDeviceId);
            }).catch(() => {
            });

            if (Product.Sport) {
                this.startOffer();
            }
            window.addEventListener('message', this.translateForChild);
            this.$watch(
                ({ modal }) => !!modal && isModalShownOnCurrentRoute(this.$route, modal),
                (hasOverlay) => {
                    const body = document.querySelector('body');
                    const header = document.querySelector('.header--fixed');
                    const scrollbarWidth = getScrollbarWidth();
                    const bodyClass = 'modal-active';
                    const originalPosition = body?.style.position;
                    if (hasOverlay) {
                        // Safari hackery, it gets broken if you go into position: fixed
                        if (isMobileSafari()) {
                            this.rememberedScrollOffset = Math.abs(window.scrollY);
                            window.scrollTo({ top: this.rememberedScrollOffset });
                        }

                        body?.classList.add(bodyClass);
                        if (scrollbarWidth > 0) {
                            if (body) {
                                body.style.paddingRight = `${scrollbarWidth}px`;
                            }
                            if (header) {
                                header.style.paddingRight = `${scrollbarWidth}px`;
                            }
                        }
                    } else {
                        body?.classList.remove(bodyClass);
                        body?.style.removeProperty('padding-right');
                        header?.style.removeProperty('padding-right');

                        // Safari hackery, it gets broken if you go into position: fixed
                        if (isMobileSafari() && this.rememberedScrollOffset) {
                            body.style.position = originalPosition;
                            body.style.top = '';
                            window?.scrollTo({ top: this.rememberedScrollOffset });
                            this.rememberedScrollOffset = undefined;
                        }
                    }
                },
            );

            // TODO: Remove, temporary for smooth migration to SDS v4
            // Gaming MFE handles this in parallel, but we don't want to lock a release cycle
            const htmlElement = document.querySelector('html');

            const napoleonThemeObserver = new MutationObserver((mutationList) => {
                const theme = (mutationList[0]?.target)?.dataset?.theme;

                if (theme === 'dark-napoleon') {
                    setTimeout(() => {
                        htmlElement.dataset.theme = Theme.Dark;
                        htmlElement.dataset.brand = Brand.NapoleonGaming;
                    }, 0);
                }
            });
            // End TODO

            napoleonThemeObserver.observe(htmlElement, {
                attributes: true,
                attributeFilter: ['data-theme'],
            });

            errorGuard(async () => {
                await this.initializeSession();
            });

            StoreUtil.createClearCopiedTicketWatcher(this.$store);

            // Move to AJ federation once federated
            this.$store.dispatch('navigation/addMobileRouteActionItems', {
                [RouteName.Account.MyAccount]: [
                    {
                        iconName: 'status-help',
                        onClick: (_route, router) => {
                            router.push({
                                name: RouteName.Account.HelpAndSupport,
                            });
                        },
                    },
                    {
                        iconName: 'navigation-settings',
                        onClick: (_route, router) => {
                            router.push({
                                name: RouteName.Account.Settings,
                            });
                        },
                    },
                ],
                [RouteName.Account.Register]: [
                    {
                        iconName: 'status-help',
                        onClick: (_route, router) => {
                            router.push({
                                name: RouteName.Account.HelpAndSupport,
                            });
                        },
                    },
                ],
                [RouteName.Account.IdentityVerification]: [
                    {
                        iconName: 'status-help',
                        onClick: () => navigateToWiki(WikiPath.Help),
                    },
                ],
                [RouteName.Account.IdentityVerificationUploadAdditional]: [
                    {
                        iconName: 'status-help',
                        onClick: () => navigateToWiki(WikiPath.Help),
                    },
                ],
            });

            adjustNapoleonFavicons();

            // Ids of corresponding fields in zendesk to update them using the set:conversation fields
            const zendeskMetadataFields = await featureFlagService.getFeatureValue(AccountFlagNames.zendeskMetadata);
            if (zendeskMetadataFields) {
                this.registerZendeskFlags(zendeskMetadataFields);
            }
            // Load zendesk on page load
            const featureSubscriptionUnsubscribeFn = featureFlagService.subscribeToFeatureChanges(
                AccountFlagNames.isLoadChatOnPageLoad,
                (isEnabled) => {
                    if (isEnabled) {
                        this.initializeHelpBot({ openHidden: true });
                    }
                },
                false,
            );
            setTimeout(() => {
                featureSubscriptionUnsubscribeFn();
            }, 3000);
        },
        computed: {
            ...mapGetters('ui/modal', ['modal']),
            ...mapGetters('data/authentication', ['user', 'isCheckedAuthentication']),
            ...mapGetters('data/maintenance', ['maintainedParts']),
            ...mapGetters('data/experiment', ['experimentService']),
            isLoading() {
                return (shouldWaitForRouteRegistration(this.$route.name) && !areRoutesRegistered());
            },
        },
        watch: {
            user: 'loadExperiments',
            isCheckedAuthentication: 'loadExperiments',
            experimentService: 'loadExperiments',
            maintainedParts: {
                deep: true,
                handler(current, previous) {
                    const flags = [MaintenanceType.Live, MaintenanceType.Prematch];
                    const isMaintenanceChanged = flags.some((flag) => previous[flag] !== current[flag]);
                    if (isMaintenanceChanged) {
                        this.$store.dispatch('ui/sportOffer/betSlip/validateBetSlip', false, { root: true });
                    }

                    const isMaintenanceTurnedOff = flags.some((flag) => previous[flag] && !current[flag]);
                    if (isMaintenanceTurnedOff) {
                        this.$store.dispatch('data/sportOffer/fetchAllOffer', undefined, { root: true });
                    }
                },
            },
        },
        methods: {
            ...mapActions('data/country', [
                'initializeConfig',
                'fetchPaymentProvidersLogos',
            ]),
            ...mapActions('data/sportOffer', [
                'startOffer',
                'startLiveEventsCountingOnly',
            ]),
            ...mapActions('data/authentication', [
                'initializeSession',
            ]),
            ...mapActions('ui/help', [
                'showHelp',
                'initializeHelpBot',
                'registerZendeskFlags',
            ]),
            ...mapActions('ui/modal', [
                'setModal',
            ]),
            ...mapActions('data/experiment', ['setIsLoading']),
            loadExperiments() {
                loadExperimentService({
                    user: this.user,
                    isCheckedAuthentication: this.isCheckedAuthentication,
                    service: this.experimentService,
                    setIsLoading: this.setIsLoading,
                });
            },
            translateForChild(event) {
                if (event.origin === window.location.origin && event.data.event === 'translate' && event.data.translations) {
                    window.postMessage({
                        event: 'translations',
                        translations: event.data.translations.reduce((resultObject, translation) => {
                            // eslint-disable-next-line no-param-reassign
                            resultObject[translation] = this.$t(translation);
                            return resultObject;
                        }, {}),
                    });
                }
            },
        },
        directives,
        components: {
            ModalContainer,
            Layout,
            AppLoadingLayout,
            UseFreeToPlayEligibilityChecker,
            SdsTooltipOverlay,
        },
    };
</script>
